import { LandingHeader } from "@/components/landing/LandingHeader";
import { useBranding } from "@/contexts/BrandingContext";
import { Lock, KeyRound, ShieldCheck, FileText, Server, EyeOff, Award, Globe2 } from "lucide-react";
import { useStaticPage, StaticPageRenderer } from "@/components/StaticPageRenderer";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// ISO / IEC 27002 + SOC 2 statements (master doc) — anchored for /data-security#iso and #soc2.
const CERTIFICATIONS = [
    {
        id: "iso",
        icon: Award,
        eyebrow: "ISO / IEC 27002",
        title: "Your family's legacy, protected by our commitment to information security",
        paras: [
            "At Humanity First Software Technology Pvt. Ltd., we believe that every family's history, memories, photographs, traditions and personal information deserve the highest standards of care and protection.",
            "Our Family Repository services have undergone an ISO/IEC 27002-based information security audit, reflecting our commitment to implementing and evaluating recognised information security controls and protocols.",
            "Our approach focuses on protecting the confidentiality, integrity and availability of the information entrusted to our platform. For us, information security is more than a technical requirement — it is a fundamental commitment to the trust that families place in our services.",
        ],
    },
    {
        id: "soc2",
        icon: Globe2,
        eyebrow: "SOC 2 Compliance",
        title: "Building trust across borders",
        paras: [
            "We understand that the trust of our NRI customers is fundamental to preserving their family's most valuable digital assets. Our platform is committed to robust information security practices, protecting sensitive family information and safeguarding personal memories, photographs, documents and family histories.",
            "Our SOC 2 compliance demonstrates our commitment to internationally recognised standards for security, confidentiality and responsible data management — giving customers in the United States, Europe and across the world greater confidence in our services.",
            "Your family's legacy. Your privacy. Our commitment.",
        ],
    },
];

const PRINCIPLES = [
    { icon: Lock,         title: "End-to-end encryption",          body: "Photos, voice notes and legal documents are encrypted at rest using AES-256 and in transit via TLS 1.3." },
    { icon: KeyRound,     title: "Biometric access",               body: "Sensitive vaults (legal, financial) require Touch / Face / Windows Hello unlock — your phone's biometric never leaves your device." },
    { icon: ShieldCheck,  title: "OTP + httpOnly session cookies", body: "We never store passwords; only an SMS OTP starts your session, kept in a short-lived httpOnly cookie immune to JavaScript-based XSS attacks." },
    { icon: Server,       title: "Data stored in India",           body: "All MongoDB clusters run inside Indian data centers, fully compliant with the DPDP Act 2023." },
    { icon: FileText,     title: "Full DPDP request portal",       body: "Download all your data or request deletion any time from /settings — admin processes requests within 7 days as required by law." },
    { icon: EyeOff,       title: "Granular privacy controls",      body: "Per-section, per-member privacy: choose who sees marriage records, who sees health records, who sees legal documents." },
];

export default function DataSecurity() {
    useDocumentMeta("Data Security & Privacy — Family Repository", "Bank-grade encryption, DPDP compliance and India-first data protection for your family's most precious records.");
    const { app_name } = useBranding();
    const data = useStaticPage("data-security");
    const location = useLocation();
    // Deep links (#iso / #soc2) — scroll once the page content has loaded.
    useEffect(() => {
        if (!data || !location.hash) return;
        const el = document.getElementById(location.hash.slice(1));
        if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    }, [data, location.hash]);
    return (
        <div className="min-h-screen relative bg-[#DCEBFB]" data-testid="data-security-page">
            {/* Security image fixed on the RIGHT (family/shield visible); the light-blue LEFT
                area carries all the text. Colours match the image's blue palette. */}
            <div
                className="fixed inset-0 z-0 pointer-events-none"
                style={{
                    backgroundImage: "url(/data-security-hero.jpg)",
                    backgroundSize: "cover",
                    backgroundPosition: "right center",
                    backgroundRepeat: "no-repeat",
                }}
                aria-hidden="true"
            />

            <div className="relative z-10">
                <LandingHeader />

                {/* All text pinned to the LEFT column */}
                <main className="max-w-[600px] px-5 sm:px-8 md:pl-[5vw] lg:pl-[7vw] pt-12 pb-20" data-testid="data-security-hero">
                    <h1
                        className="text-4xl md:text-5xl font-bold text-[#0B2545] tracking-tight leading-[1.08]"
                        data-testid="data-security-hero-title"
                    >
                        Data Security &amp; Privacy
                    </h1>
                    <p
                        className="mt-4 text-[15.5px] md:text-[17px] text-[#274863] font-medium leading-relaxed"
                        data-testid="data-security-hero-subtitle"
                    >
                        Your family&apos;s memories are sacred. Here&apos;s how {app_name} keeps them safe.
                    </p>

                    <StaticPageRenderer
                        data={data}
                        hideHero
                        defaultLayout={
                            <>
                                <div className="mt-10 space-y-6">
                                    {PRINCIPLES.map(({ icon: Icon, title, body }) => (
                                        <div key={title} className="flex gap-3.5">
                                            <Icon className="h-6 w-6 shrink-0 text-[#1E5FBF] mt-0.5" />
                                            <div>
                                                <h3 className="text-[16.5px] font-bold text-[#0B2545]">{title}</h3>
                                                <p className="mt-1 text-[14px] text-[#274863] leading-relaxed">{body}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-12 space-y-8" data-testid="data-security-certifications">
                                    {CERTIFICATIONS.map(({ id, icon: Icon, eyebrow, title, paras }) => (
                                        <section key={id} id={id} data-testid={`data-security-${id}`} className="scroll-mt-28 rounded-2xl bg-white/70 border border-[#1E5FBF]/15 p-5 sm:p-6">
                                            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#1E5FBF] font-semibold">
                                                <Icon className="h-4 w-4" /> {eyebrow}
                                            </div>
                                            <h2 className="mt-2 text-[19px] font-bold text-[#0B2545] leading-snug">{title}</h2>
                                            {paras.map((p, i) => (
                                                <p key={i} className="mt-2.5 text-[14px] text-[#274863] leading-relaxed">{p}</p>
                                            ))}
                                        </section>
                                    ))}
                                </div>

                                <div className="mt-10 space-y-4" data-testid="data-security-related-policies">
                                    <a href="/data-encryption" data-testid="ds-link-encryption" className="block group">
                                        <div className="text-[11px] uppercase tracking-wider text-[#1E5FBF] font-semibold">Policy</div>
                                        <div className="mt-0.5 text-[15px] font-bold text-[#0B2545] group-hover:underline">Encryption Policy →</div>
                                        <div className="mt-0.5 text-[12.5px] text-[#274863]">AES-256, TLS 1.3, key rotation, RBAC, MFA — the full technical detail.</div>
                                    </a>
                                    <a href="/data-protection" data-testid="ds-link-protection" className="block group">
                                        <div className="text-[11px] uppercase tracking-wider text-[#1E5FBF] font-semibold">Policy</div>
                                        <div className="mt-0.5 text-[15px] font-bold text-[#0B2545] group-hover:underline">Data Protection Policy →</div>
                                        <div className="mt-0.5 text-[12.5px] text-[#274863]">DPDP + GDPR principles, data minimization, your rights as a data principal.</div>
                                    </a>
                                    <a href="/dpdp-compliance" data-testid="ds-link-dpdp" className="block group">
                                        <div className="text-[11px] uppercase tracking-wider text-[#1E5FBF] font-semibold">Policy</div>
                                        <div className="mt-0.5 text-[15px] font-bold text-[#0B2545] group-hover:underline">DPDP Act Compliance →</div>
                                        <div className="mt-0.5 text-[12.5px] text-[#274863]">How we align with India&apos;s Digital Personal Data Protection Act, 2023.</div>
                                    </a>
                                </div>

                                <div className="mt-10">
                                    <h2 className="text-[19px] font-bold text-[#0B2545]">Ethical AI Usage</h2>
                                    <p className="mt-1.5 text-[14px] text-[#274863] leading-relaxed">
                                        We use AI to assist, never replace, your storytelling. Full transparency, strict data privacy, human oversight on every output.
                                    </p>
                                    <a
                                        href="/ethical-ai"
                                        data-testid="data-security-ethical-ai-link"
                                        className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#1E5FBF] underline hover:text-[#0B2545]"
                                    >
                                        Read our Ethical AI Policy →
                                    </a>
                                </div>

                                <div className="mt-10">
                                    <h2 className="text-[20px] font-bold text-[#0B2545]">Have a security question?</h2>
                                    <p className="mt-1.5 text-[14px] text-[#274863]">
                                        Email <a href="mailto:Support@familyrepository.org" className="underline text-[#1E5FBF] font-semibold">Support@familyrepository.org</a> —
                                        our security team responds within 24 hours.
                                    </p>
                                </div>
                            </>
                        }
                    />
                </main>
            </div>
        </div>
    );
}
